import { useCallback, useState } from 'react'
import { cambiarContratoSesion } from '@/common/utils/services/contratos.service'
import { getIdUsuario, getUserToken } from '@/auth/auth.utils'
import { useAuthStore } from '@/common/zustand/store/auth.store'
import { buildWelcomeUrl } from '@/common/utils/navigation/welcome-url.js'
import { normalizarCuit } from '../util/formatearCuit.js'
import { MENSAJES_INGRESO } from '../constants/textos.js'

/**
 * Cambia la cuenta con la que opera el usuario y lo lleva a la bienvenida del
 * PHP (criterios 5.1 y 5.2).
 *
 * `POST /v1/login/contrato` actualiza la sesión en el backend pero no devuelve
 * el contrato nuevo. Se relee con `checkAuth` para que la cookie y el store
 * queden alineados antes de salir del SPA.
 *
 * @param {Object} opciones
 * @param {(variant: 'error', mensaje: string) => void} opciones.onMensaje
 */
export const useIngresarCuenta = ({ onMensaje } = {}) => {
  const checkAuth = useAuthStore((s) => s.checkAuth)
  const [contratoIngresando, setContratoIngresando] = useState(null)

  const ingresarCuenta = useCallback(
    async (cuenta) => {
      const uuid = getUserToken()
      const idUsuario = getIdUsuario()

      if (!uuid || !idUsuario) {
        onMensaje?.('error', MENSAJES_INGRESO.ERROR)
        return
      }

      setContratoIngresando(Number(cuenta?.contrato))

      try {
        const resultado = await cambiarContratoSesion({
          uuid,
          idUsuario: Number(idUsuario),
          contrato: Number(cuenta?.contrato),
          cuit: normalizarCuit(cuenta?.cuit),
        })

        if (resultado.code < 200 || resultado.code >= 300) {
          onMensaje?.('error', resultado.message || MENSAJES_INGRESO.ERROR)
          setContratoIngresando(null)
          return
        }

        const sesionOk = await checkAuth({ uuid, skipCertValidation: true })

        // checkAuth ya hizo logout y está navegando por su cuenta: no pisamos
        // ese destino, o el usuario caería en la bienvenida sin sesión válida.
        if (!sesionOk) return

        // El estado de carga no se limpia: la navegación no es instantánea y
        // devolver el link a "Ingresar" daría la sensación de que no pasó nada.
        window.location.replace(buildWelcomeUrl(import.meta.env.VITE_PHP_REDIRECT))
      } catch (error) {
        console.error('Error al cambiar de cuenta:', error)
        onMensaje?.('error', MENSAJES_INGRESO.ERROR_RED)
        setContratoIngresando(null)
      }
    },
    [checkAuth, onMensaje]
  )

  return {
    ingresarCuenta,
    contratoIngresando,
    ingresoEnCurso: contratoIngresando !== null,
  }
}
